import { Injectable } from '@angular/core'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { AppSettingsService } from 'src/app/settings/services/app-settings.service'

export interface SideMenuItem {
  name: string
  link: string
  externalURL?: string
}

/** Default side menu design. Empty color / icon values mean "use the app default". */
export const DEFAULT_SIDE_MENU_DESIGN = {
  data: {
    // Off by default: the app keeps the classic side menu until this is turned on
    useNewDesign: false,
    title: 'We are here to help!',
    subtitle: 'We are here to support you every step of the way. Feel free to contact us.',
    socialTitle: 'Contact us',
    welcomeText: 'Welcome',
    itemIcon: '',
    logo: '',
    showLogo: true,
    showTitle: true,
    showSocial: true,
    showUserFooter: true,
    showDecoration: true
  },
  style: {
    maxWidth: 450,
    backgroundColor: '#ffffff',
    cardColor: '#ffffff',
    cardBorderColor: '#c3c6ce',
    iconBoxColor: '#f2f4f6',
    textColor: '#191c1e',
    mutedTextColor: '#43474d',
    accentColor: ''
  }
}

/**
 * The app side menu: one design + one list of items for the whole project (all pages).
 * Edited from the Side Menu block and saved right away to general_settings:
 *   - menus      -> the menu items
 *   - side_menu  -> the design { data, style }
 */
@Injectable({ providedIn: 'root' })
export class SideMenuItemsService {
  items: SideMenuItem[] = []
  design: any = JSON.parse(JSON.stringify(DEFAULT_SIDE_MENU_DESIGN))
  loaded = false
  saving = false
  private loadedProject: any = null
  private saveTimer: any
  private pending: Record<string, any> = {}

  /** Link types the app understands (see buttonClicked in the app) */
  readonly linkTypes = [
    { value: 'home', label: 'Home' },
    { value: 'allProductsCategories', label: 'All Products Categories' },
    { value: 'externalURL', label: 'External URL' },
    { value: 'account', label: 'Account Page' },
    { value: 'signup', label: 'Signup / Signin' },
    { value: 'contact', label: 'Contact Us' },
    { value: 'about', label: 'About Us' },
    { value: 'orders', label: 'Orders' },
    { value: 'cart', label: 'Cart' },
    { value: 'terms', label: 'Terms' }
  ]

  constructor(
    private builderService: BuilderService,
    private appSettingsService: AppSettingsService
  ) {}

  /** Load once per project */
  load() {
    const projectId = this.builderService.getSelectedProject()
    if (this.loadedProject === projectId) return
    this.loadedProject = projectId
    this.loaded = false
    this.appSettingsService.getSettingsByType(String(projectId), 'general_settings').then((settings: any) => {
      const menus = this.parse(settings?.menus)
      const design = this.parse(settings?.side_menu)
      this.items = Array.isArray(menus) ? menus : []
      this.design = {
        data: { ...DEFAULT_SIDE_MENU_DESIGN.data, ...(design?.data || {}) },
        style: { ...DEFAULT_SIDE_MENU_DESIGN.style, ...(design?.style || {}) }
      }
      this.loaded = true
      this.syncGlobals()
    })
  }

  /*** Items ***/
  add(name: string, link: string) {
    if (!name?.trim()) return
    this.items = [...this.items, { name: name.trim(), link: link || 'home' }]
    this.saveItems()
  }

  update(index: number, changes: Partial<SideMenuItem>) {
    this.items = this.items.map((item, i) => (i === index ? { ...item, ...changes } : item))
    this.saveItems()
  }

  remove(index: number) {
    this.items = this.items.filter((_, i) => i !== index)
    this.saveItems()
  }

  move(from: number, to: number) {
    const items = [...this.items]
    const [moved] = items.splice(from, 1)
    items.splice(to, 0, moved)
    this.items = items
    this.saveItems()
  }

  /*** Design ***/
  setData(key: string, value: any) {
    this.design = { ...this.design, data: { ...this.design.data, [key]: value } }
    this.queueSave('side_menu', this.design)
  }

  setStyle(key: string, value: any) {
    this.design = { ...this.design, style: { ...this.design.style, [key]: value } }
    this.queueSave('side_menu', this.design)
  }

  private saveItems() {
    this.syncGlobals()
    this.queueSave('menus', this.items)
  }

  /** Save shortly after the last change (typing saves once, not per key) */
  private queueSave(field: string, value: any) {
    if (!this.loaded) return
    this.pending[field] = value
    clearTimeout(this.saveTimer)
    this.saving = true
    this.saveTimer = setTimeout(() => {
      const fields = this.pending
      this.pending = {}
      this.appSettingsService
        .upsertSettingsByType(String(this.builderService.getSelectedProject()), 'general_settings', fields)
        .finally(() => (this.saving = false))
    }, 600)
  }

  /** Keep the old globals value in step so "Save globals" never writes an outdated list */
  private syncGlobals() {
    if (this.builderService.globalSettings) {
      this.builderService.globalSettings.menu = this.items
    }
  }

  private parse(value: any) {
    if (typeof value !== 'string') return value
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  }
}
